import React, { useEffect } from 'react';
import useMultiplayerState from './hooks/useMultiplayerState';
import useGameSettings from './hooks/useGameSettings';
import useLobbyHandlers from './hooks/useLobbyHandlers';
import useGameLogic from './hooks/useGameLogic';
import useSound from './hooks/useSound';
import GameRenderer from './components/GameRenderer';

function App() {
  // Custom hooks
  const multiplayerState = useMultiplayerState();
  const gameSettings = useGameSettings();
  const playSound = useSound(gameSettings.muted);
  const gameLogic = useGameLogic(
    gameSettings.selectedCategory,
    gameSettings.selectedDifficulty,
    gameSettings.questionCount,
    gameSettings.muted,
    playSound
  );
  const handlers = useLobbyHandlers(multiplayerState, gameSettings, gameLogic);

  // Log app state changes
  useEffect(() => {
    console.log('[App] State changed', {
      gameMode: multiplayerState.gameMode,
      gameState: gameLogic.gameState,
      selectedCategory: gameSettings.selectedCategory,
      selectedDifficulty: gameSettings.selectedDifficulty,
      questionCount: gameSettings.questionCount,
      muted: gameSettings.muted,
      hasGameData: !!gameLogic.gameData,
      currentQuestionIndex: gameLogic.currentQuestionIndex,
      score: gameLogic.score,
      loading: gameLogic.loading,
      hasError: !!gameLogic.error,
      lobbyCode: multiplayerState.lobbyCode,
      playerName: multiplayerState.playerName,
      playersCount: multiplayerState.players.length,
      isHost: multiplayerState.isHost,
      playerToken: multiplayerState.playerToken ? 'present' : 'missing'
    });
  }, [
    multiplayerState.gameMode,
    gameLogic.gameState,
    gameSettings.selectedCategory,
    gameSettings.selectedDifficulty,
    gameSettings.questionCount,
    gameSettings.muted,
    gameLogic.gameData,
    gameLogic.currentQuestionIndex,
    gameLogic.score,
    gameLogic.loading,
    gameLogic.error,
    multiplayerState.lobbyCode,
    multiplayerState.playerName,
    multiplayerState.players.length,
    multiplayerState.isHost,
    multiplayerState.playerToken
  ]);

  return (
    <div className="container">
      {/* Debug Panel - only show in development */}
      {typeof window !== 'undefined' && window.location.hostname === 'localhost' && (
        <div style={{
          position: 'fixed',
          top: '10px',
          right: '10px',
          background: 'rgba(0,0,0,0.8)',
          color: 'white',
          padding: '10px',
          borderRadius: '5px',
          fontSize: '12px',
          zIndex: 1000,
          maxWidth: '300px',
          maxHeight: '300px',
          overflow: 'auto'
        }}>
          <div><strong>Debug Info:</strong></div>
          <div>Mode: {multiplayerState.gameMode}</div>
          <div>State: {gameLogic.gameState}</div>
          <div>Loading: {gameLogic.loading ? 'Yes' : 'No'}</div>
          <div>Error: {gameLogic.error || 'None'}</div>
          <div>Score: {gameLogic.score}</div>
          <div>Question: {gameLogic.currentQuestionIndex + 1}</div>
          <div>Game ID: {gameLogic.gameData?.gameId?.substring(0, 8) || 'None'}</div>
          <div>Lobby: {multiplayerState.lobbyCode || 'None'}</div>
          <div>Player: {multiplayerState.playerName || 'None'}</div>
          <div>Players: {multiplayerState.players.length}</div>
          <div>Host: {multiplayerState.isHost ? 'Yes' : 'No'}</div>
          <div>Category: {gameSettings.selectedCategory}</div>
          <div>Difficulty: {gameSettings.selectedDifficulty}</div>
        </div>
      )}
      
      {/* Main Game Content */}
      <GameRenderer
        gameMode={multiplayerState.gameMode}
        gameLogic={gameLogic}
        multiplayerState={multiplayerState}
        gameSettings={gameSettings}
        handlers={handlers}
      />
    </div>
  );
}

export default App;